import config from 'config';
import { AgentScannerService } from '@mulesoft/agent-scanner-client';

/**
 * Helper to determine if code is running on the client (browser) vs server (Node.js/SSR).
 * Returns true if `window` object exists (browser environment),
 * false if running in Node.js (server-side rendering).
 */
const isClient = () => typeof window !== 'undefined';

/**
 * Create proxy agent for server-side requests if proxy is enabled
 * This is required for HyperForce environments (stgx, prod) where
 * pods cannot access the internet directly.
 *
 * Note: We use __non_webpack_require__ to load https-proxy-agent only on the server.
 * This tells Webpack to ignore this require and leave it for Node.js runtime,
 * preventing it from trying to bundle Node.js-only modules (net, tls, assert).
 */
const createProxyAgent = () => {
  // Only run on server (SSR), never in browser
  if (isClient()) {
    return undefined;
  }

  const proxyConfig = config.scannerAPI?.proxy;

  if (proxyConfig?.enabled && proxyConfig?.host?.trim()) {
    try {
      // __non_webpack_require__ tells Webpack to skip this and leave it for Node.js
      /* eslint-disable camelcase, no-undef */
      const nodeRequire =
        typeof __non_webpack_require__ !== 'undefined'
          ? __non_webpack_require__
          : require;
      /* eslint-enable camelcase, no-undef */
      const { HttpsProxyAgent } = nodeRequire('https-proxy-agent');

      // HTTP_PROXY env var contains full URL (e.g., http://proxy:8443)
      // If host already has scheme, use it directly; otherwise build the URL
      const proxyURL = proxyConfig.host.startsWith('http')
        ? proxyConfig.host
        : `http://${proxyConfig.host}:8443`;

      return new HttpsProxyAgent(proxyURL);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create proxy agent:', error.message);

      return undefined;
    }
  }

  return undefined;
};

/**
 * Client-side service instance (browser)
 * Uses relative URLs (/agent-scanner/...) that go through the Express proxy
 * The proxy then forwards to config.scannerAPI
 */
const clientService = new AgentScannerService({
  api: {
    uri: {
      pathname: '' // Empty -> relative URLs like /agent-scanner/api/v1/...
    }
  },
  skipProxyPrefix: false // Browser: add /agent-scanner prefix (proxy intercepts)
});

/**
 * Server-side service instance (SSR)
 * Uses absolute URLs directly to the backend (config.scannerAPI)
 * skipProxyPrefix=true means URLs are /api/v1/... (no /agent-scanner prefix)
 * proxyAgent is used for HyperForce environments where direct internet access is blocked
 */
const serverService = new AgentScannerService({
  api: config.scannerAPI, // Full backend URL (e.g., https://stgx.anypoint.mulesoft.com/agent-scanner)
  skipProxyPrefix: true, // SSR: don't add /agent-scanner prefix, call backend directly
  // createProxyAgent() returns undefined unless SCANNER_API_PROXY_ENABLED=true (HyperForce only)
  proxyAgent: createProxyAgent()
});

/**
 * Get the appropriate service instance based on execution context
 * @returns {AgentScannerService} Client or server service instance
 */
const getService = () => (isClient() ? clientService : serverService);

/**
 * List all agent scanners for an organization
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 10000)
 * @returns {Promise<Object>} Response with paginated scanners data
 */
export function list({ organizationId, context, page = 0, size = 10000 }) {
  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  return getService().list({ organizationId, context, page, size });
}

/**
 * Create a new agent scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {Object} params.data - Scanner configuration data
 * @returns {Promise<Object>} Response with created scanner data
 */
export function create({ organizationId, context, data }) {
  return getService().create({ organizationId, data, context });
}

/**
 * Update an existing agent scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to update
 * @param {Object} params.context - Request context with authorization
 * @param {Object} params.data - Scanner configuration data
 * @returns {Promise<Object>} Response with updated scanner data
 */
export function update({ organizationId, scannerId, context, data }) {
  return getService().update({ organizationId, scannerId, data, context });
}

/**
 * Delete an agent scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to delete
 * @param {Object} params.context - Request context with authorization
 * @param {boolean} params.deleteAgents - If true, also deletes associated agents (default: false)
 * @returns {Promise<void>} Promise that resolves when deletion is complete
 */
export function deleteScanner({
  organizationId,
  scannerId,
  context,
  deleteAgents = false
}) {
  return getService().delete({
    organizationId,
    scannerId,
    context,
    deleteAgents
  });
}

/**
 * Execute a scanner configuration workflow
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to execute
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<void>} Promise that resolves when execution is triggered
 */
export function execute({ organizationId, scannerId, context }) {
  return getService().execute({ organizationId, scannerId, context });
}

/**
 * Abort a running scanner configuration
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.scannerId - Scanner ID to abort
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<void>} Promise that resolves when abort is triggered
 */
export function abort({ organizationId, scannerId, context }) {
  return getService().abort({ organizationId, scannerId, context });
}

/**
 * List scanner runs (history) for a specific scanner
 * @param {Object} params - Parameters for the request
 * @param {string} params.scannerId - Scanner ID
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 20)
 * @returns {Promise<Object>} Response with paginated scanner runs data
 */
export function listRuns({
  scannerId,
  organizationId,
  context,
  page = 0,
  size = 20
}) {
  return getService().listRuns({
    scannerId,
    organizationId,
    context,
    page,
    size
  });
}

/**
 * Get available target systems for connections
 * @param {Object} params - Parameters for the request
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<Array>} Response with list of target systems
 */
export function getTargetSystems({ context }) {
  return getService().getTargetSystems({ context });
}

/**
 * Test connection to a target system
 * @param {Object} params - Parameters for the request
 * @param {string} params.targetSystemType - Target system type (e.g., 'bedrock', 'openai')
 * @param {Object} params.connectionParams - Connection parameters (includes id field)
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<boolean>} True if connection successful, false otherwise
 */
export function testConnection({
  targetSystemType,
  connectionParams,
  context
}) {
  return getService().testConnection({
    targetSystemType,
    connectionParams,
    context
  });
}

/**
 * List Stagin Agents for a specific scanner run
 * @param {Object} params - Parameters for the request
 * @param {string} params.scanRunId - Scanner Run ID
 * @param {string} params.scannerId - Scanner ID
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 20)
 * @returns {Promise<Object>} Response with paginated scanner runs data
 */
export function getStagingAgents({
  organizationId,
  scannerId,
  scanRunId,
  context,
  page = 0,
  size = 20
}) {
  return getService().getStagingAgents({
    organizationId,
    scannerId,
    scanRunId,
    context,
    page,
    size
  });
}

/**
 * Get organization info from GET /api/v1/organizations/{organizationId}/info.
 * Response format: { totalManagedAgents, capacityAgentsLimit }
 * Used to display agent capacity (used / total); returns null when the API is unavailable.
 *
 * @param {Object} params - Parameters for the request
 * @param {string} params.organizationId - Organization ID
 * @param {Object} params.context - Request context with authorization
 * @returns {Promise<{ used: number, total: number }|null>} Capacity { used, total } or null if unavailable
 */
export function getOrganizationInfo({ organizationId, context }) {
  if (!organizationId) {
    return Promise.resolve(null);
  }

  const service = getService();

  if (typeof service.getOrganizationInfo !== 'function') {
    return Promise.resolve(null);
  }

  return service
    .getOrganizationInfo({ organizationId, context })
    .then((data) => {
      const used = data?.totalManagedAgents;
      const total = data?.capacityAgentsLimit;

      if (typeof used === 'number' && typeof total === 'number') {
        return { used, total };
      }

      return null;
    })
    .catch(() => null);
}
